import React, { useState, useEffect, useCallback } from 'react';
import { Live2DStage } from '../live2d';
import { UI_DEMO_LABELS } from '../../game/fixtures/uiKitFixtures';
import { GAME_CHEAT_ADD_TOKENS } from '../../game/gamePrefs';
import { buildLive2dModelUrlFromParts } from '../../utils/live2dUrls';

const SPEEDS = [
  { value: 1, label: '1x' },
  { value: 10, label: '10x' },
  { value: 100, label: '100x' }
];

const CHARACTERS = ['Lina', 'Mortifera'];

const PUBLIC = typeof process !== 'undefined' ? process.env.PUBLIC_URL || '' : '';

function motionLabel(file) {
  if (!file || typeof file !== 'string') return '—';
  const base = file.split('/').pop() || file;
  return base.replace(/\.motion3\.json$/i, '');
}

export default function GameCheatPanel({
  open,
  onOpenChange,
  live2dSpeed,
  onLive2dSpeedChange,
  onPickUiDemo,
  /** When true, parent should unmount its own Live2DStage (second Cubism instance often breaks preview). */
  onLive2dPreviewDockVisibilityChange
}) {
  const [subView, setSubView] = useState('main');
  const [character, setCharacter] = useState('Lina');
  const [indexData, setIndexData] = useState(null);
  const [indexErr, setIndexErr] = useState('');
  const [modelPath, setModelPath] = useState('');
  const [modelJson, setModelJson] = useState(null);
  const [modelErr, setModelErr] = useState('');
  const [motionIndex, setMotionIndex] = useState(0);
  const [live2dPreview, setLive2dPreview] = useState(null);

  const resetLive2dFlow = useCallback(() => {
    setSubView('main');
    setIndexData(null);
    setIndexErr('');
    setModelPath('');
    setModelJson(null);
    setModelErr('');
    setMotionIndex(0);
    setLive2dPreview(null);
  }, []);

  const enterLive2dMode = () => {
    setSubView('live2d');
  };

  const backToMainCheats = () => {
    setSubView('main');
    setLive2dPreview(null);
  };

  useEffect(() => {
    if (!open) {
      resetLive2dFlow();
    }
  }, [open, resetLive2dFlow]);

  useEffect(() => {
    if (subView !== 'live2d' || !open) return;
    setModelPath('');
    setModelJson(null);
    setModelErr('');
    let cancel = false;
    setIndexErr('');
    setIndexData(null);
    (async () => {
      try {
        const res = await fetch(`${PUBLIC}/live2d/${encodeURIComponent(character)}/index.json`, {
          cache: 'no-store'
        });
        if (!res.ok) throw new Error('index');
        const data = await res.json();
        if (cancel) return;
        setIndexData(data);
        const first = data?.models?.[0]?.path;
        if (first) setModelPath(first);
        else setModelPath('');
      } catch {
        if (!cancel) setIndexErr('Failed to load index');
      }
    })();
    return () => {
      cancel = true;
    };
  }, [subView, open, character]);

  useEffect(() => {
    if (subView !== 'live2d' || !open || !modelPath) {
      setModelJson(null);
      setModelErr('');
      return undefined;
    }
    const ac = new AbortController();
    setModelErr('');
    setModelJson(null);
    const url = buildLive2dModelUrlFromParts(PUBLIC, character, modelPath);
    (async () => {
      try {
        const res = await fetch(url, { signal: ac.signal, cache: 'no-store' });
        if (!res.ok) throw new Error('model');
        const data = await res.json();
        if (ac.signal.aborted) return;
        setModelJson(data);
        setMotionIndex(0);
      } catch {
        if (ac.signal.aborted) return;
        setModelErr('Failed to load model');
      }
    })();
    return () => ac.abort();
  }, [subView, open, character, modelPath]);

  const idleMotions = modelJson?.FileReferences?.Motions?.idle || [];

  useEffect(() => {
    if (subView !== 'live2d' || !open || !modelPath) {
      setLive2dPreview(null);
      return;
    }
    if (modelErr || !modelJson) {
      setLive2dPreview(null);
      return;
    }
    const n = idleMotions.length;
    const idx = n ? Math.min(motionIndex, n - 1) : 0;
    const modelUrl = buildLive2dModelUrlFromParts(PUBLIC, character, modelPath);
    setLive2dPreview({
      modelUrl,
      motionIndex: idx,
      motionGroup: 'idle'
    });
  }, [
    subView,
    open,
    character,
    modelPath,
    motionIndex,
    modelJson,
    modelErr,
    idleMotions.length
  ]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const panelTitle = subView === 'live2d' ? 'Live2D · Animations' : 'Cheats';
  const panelWide = subView === 'live2d';
  const showPreviewDock = open && subView === 'live2d';

  useEffect(() => {
    onLive2dPreviewDockVisibilityChange?.(showPreviewDock);
  }, [showPreviewDock, onLive2dPreviewDockVisibilityChange]);

  return (
    <>
      <button
        type="button"
        onClick={() => onOpenChange(true)}
        className="w-full rounded-md border border-white/15 bg-[#1a1a1f] px-2.5 py-1.5 text-[11px] font-medium text-[#c8c8d0] shadow-lg transition hover:border-white/25 hover:bg-[#222228]"
      >
        Cheats
      </button>

      <div
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-150 ${
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
        aria-hidden={!open}
        onClick={handleClose}
      />

      {/* Док справа: слева превью Live2D, справа панель читов (только в режиме Animations) */}
      <div
        className={`fixed right-0 top-0 z-50 flex h-full flex-row shadow-2xl transition-transform duration-200 ease-out ${
          open ? 'pointer-events-auto translate-x-0' : 'pointer-events-none translate-x-full'
        }`}
        aria-hidden={!open}
        onClick={(e) => e.stopPropagation()}
      >
        {showPreviewDock ? (
          <div className="flex h-full min-h-0 w-[min(40vw,420px)] min-w-[220px] max-w-[520px] shrink-0 flex-col border-l border-white/10 bg-[#141418]">
            <div className="shrink-0 border-b border-white/10 px-2.5 py-1.5">
              <p className="text-[9px] font-semibold uppercase tracking-wide text-[#8888a0]">Live2D preview</p>
            </div>
            {/* Parent must be a flex column so Live2DStage’s flex-1 / h-full host gets a real height */}
            <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
              {live2dPreview?.modelUrl ? (
                <Live2DStage
                  key={`${live2dPreview.modelUrl}-${live2dPreview.motionIndex}`}
                  className="min-h-0 flex-1"
                  modelUrl={live2dPreview.modelUrl}
                  motionGroup={live2dPreview.motionGroup || 'idle'}
                  motionIndex={live2dPreview.motionIndex}
                  speedMultiplier={live2dSpeed}
                />
              ) : (
                <div className="flex min-h-[200px] flex-1 items-center justify-center px-3 text-center text-[10px] text-[#6a6a78]">
                  {modelErr || indexErr ? 'Could not load' : 'Loading model…'}
                </div>
              )}
            </div>
          </div>
        ) : null}

        <aside
          className={`flex h-full shrink-0 flex-col border-l border-white/10 bg-[#1a1a1f] ${
            panelWide ? 'w-[min(22rem,92vw)]' : 'w-[min(17.5rem,88vw)]'
          }`}
        >
          <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-2.5 py-2">
            <h2 className="text-xs font-semibold tracking-tight text-[#e8e8ee]">{panelTitle}</h2>
            <button
              type="button"
              onClick={handleClose}
              className="rounded px-1.5 py-0.5 text-[10px] text-[#8888a0] transition hover:bg-white/10 hover:text-[#c8c8d0]"
            >
              Close
            </button>
          </div>
          <div className="vr-scroll-y flex-1 space-y-3 overflow-y-auto px-2.5 py-2">
            {subView === 'live2d' ? (
              <>
                <button
                  type="button"
                  onClick={backToMainCheats}
                  className="w-full rounded-md border border-white/15 bg-black/30 px-2 py-1.5 text-[10px] font-semibold text-[#c8c8d0] transition hover:border-violet-500/35 hover:bg-violet-500/10"
                >
                  ← Back to cheats
                </button>

                <div>
                  <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-[#8888a0]">
                    Character
                  </p>
                  <div className="flex gap-1">
                    {CHARACTERS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => {
                          setCharacter(c);
                          setModelPath('');
                          setMotionIndex(0);
                        }}
                        className={`flex-1 rounded-md border py-1 text-[10px] font-semibold transition ${
                          character === c
                            ? 'border-violet-500/50 bg-violet-600/25 text-violet-200'
                            : 'border-white/10 bg-black/30 text-[#a0a0b0] hover:border-white/20'
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-[#8888a0]">
                    Model (index.json)
                  </p>
                  {indexErr ? (
                    <p className="text-[10px] text-red-400">{indexErr}</p>
                  ) : !indexData?.models?.length ? (
                    <p className="text-[10px] text-[#8888a0]">Loading…</p>
                  ) : (
                    <select
                      value={modelPath}
                      onChange={(e) => {
                        setModelPath(e.target.value);
                        setMotionIndex(0);
                      }}
                      className="w-full rounded-md border border-white/15 bg-black/40 px-2 py-1.5 text-[10px] text-[#e8e8ee]"
                    >
                      {indexData.models.map((m) => (
                        <option key={m.path} value={m.path}>
                          {m.path}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-[#8888a0]">
                    Animation (idle group)
                  </p>
                  {modelErr ? (
                    <p className="text-[10px] text-red-400">{modelErr}</p>
                  ) : !modelPath ? (
                    <p className="text-[10px] text-[#8888a0]">Pick a model</p>
                  ) : !idleMotions.length ? (
                    <p className="text-[10px] text-[#8888a0]">No motions in model file</p>
                  ) : (
                    <ul className="max-h-[min(50vh,320px)] space-y-0.5 overflow-y-auto rounded-md border border-white/10 bg-black/25 p-1">
                      {idleMotions.map((entry, i) => {
                        const file = entry?.File || entry?.file;
                        const active = i === motionIndex;
                        return (
                          <li key={`${file}-${i}`}>
                            <button
                              type="button"
                              onClick={() => setMotionIndex(i)}
                              className={`w-full rounded px-1.5 py-1 text-left text-[9px] font-medium leading-snug transition ${
                                active
                                  ? 'bg-violet-600/35 text-violet-100'
                                  : 'text-[#b8b8c8] hover:bg-white/5 hover:text-[#e8e8ee]'
                              }`}
                            >
                              <span className="tabular-nums text-[#8888a0]">{i}</span> {motionLabel(file)}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-[#8888a0]">
                    Tokens
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        window.dispatchEvent(
                          new CustomEvent(GAME_CHEAT_ADD_TOKENS, { detail: { amount: 1000 } })
                        );
                      }
                    }}
                    className="w-full rounded-md border border-amber-500/35 bg-amber-500/15 px-2 py-1.5 text-[10px] font-semibold text-amber-100 transition hover:border-amber-400/50 hover:bg-amber-500/25"
                  >
                    +1000 tokens
                  </button>
                </div>

                <div>
                  <button
                    type="button"
                    onClick={enterLive2dMode}
                    className="w-full rounded-md border border-cyan-500/35 bg-cyan-600/15 px-2 py-1.5 text-[10px] font-semibold text-cyan-100 transition hover:border-cyan-400/45 hover:bg-cyan-500/25"
                  >
                    Live2D · Animations
                  </button>
                  <p className="mt-1 text-[9px] leading-snug text-[#6a6a78]">
                    Opens preview in the panel to the left of this one.
                  </p>
                </div>

                <div>
                  <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-[#8888a0]">
                    Live2D speed
                  </p>
                  <div className="flex gap-1">
                    {SPEEDS.map(({ value, label }) => {
                      const active = live2dSpeed === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => onLive2dSpeedChange(value)}
                          className={`flex-1 rounded-md border py-1 text-[10px] font-semibold transition ${
                            active
                              ? 'border-violet-500/50 bg-violet-600/25 text-violet-200'
                              : 'border-white/10 bg-black/30 text-[#a0a0b0] hover:border-white/20 hover:bg-black/45'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <p className="mb-1 text-[9px] font-semibold uppercase tracking-wide text-[#8888a0]">
                    UI kit
                  </p>
                  <ul className="flex flex-col gap-0.5">
                    {UI_DEMO_LABELS.map(({ id, label }) => (
                      <li key={id}>
                        <button
                          type="button"
                          onClick={() => {
                            onPickUiDemo(id);
                            onOpenChange(false);
                          }}
                          className="w-full rounded-md border border-white/10 bg-black/25 px-2 py-1 text-left text-[10px] font-medium leading-tight text-[#c8c8d0] transition hover:border-violet-500/35 hover:bg-violet-500/10 hover:text-violet-100"
                        >
                          {label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
        </aside>
      </div>
    </>
  );
}
